import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";

export default function VerifyOtp() {
  const { loginWithToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const initialDevOtp = location.state?.devOtp || null;

  const [devOtp, setDevOtp] = useState(initialDevOtp);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      loginWithToken(data.token, data.user);
      toast.success("Account verified · signed in");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/resend-otp", { email });
      setDevOtp(data?.otp_delivery?.dev_otp || null);
      toast.success("New code sent");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout
      step="[ auth_03 ] verify"
      title="Enter your code"
      subtitle={
        <>
          We sent a 6-digit code to{" "}
          <span className="text-white">{email}</span>. It expires in 10 minutes.
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" data-testid="verify-form">
        <Input
          ref={inputRef}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="••••••"
          className="rounded-none bg-[#0a0a0a] border-neutral-800 focus-visible:border-[#E2FF54] focus-visible:ring-0 mono h-16 text-white text-center text-3xl tracking-[0.5em]"
          data-testid="verify-code"
        />

        {devOtp && (
          <div
            className="border border-[#E2FF54]/30 bg-[#E2FF54]/[0.04] p-3 mono text-xs text-[#E2FF54]"
            data-testid="dev-otp-hint"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#E2FF54]/70 mb-1">
              dev mode · MOCKED
            </div>
            code: <span className="text-lg tracking-[0.3em]">{devOtp}</span>
            <div className="text-[10px] text-neutral-500 mt-1">
              add RESEND_API_KEY to backend .env to send real emails
            </div>
          </div>
        )}

        {error && (
          <div
            className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-3 mono text-xs text-[#FF9994]"
            data-testid="verify-error"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="btn-brutal w-full rounded-none bg-[#E2FF54] text-black hover:bg-[#d4f531] mono uppercase tracking-[0.24em] text-xs py-6"
          data-testid="verify-submit"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" /> verifying…
            </>
          ) : (
            <>
              <ShieldCheck size={14} className="mr-2" /> verify & sign in
            </>
          )}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-neutral-900 mono text-xs text-neutral-500 flex items-center justify-between">
        <span>didn't get it?</span>
        <button
          onClick={resend}
          disabled={resending}
          className="text-[#E2FF54] hover:underline uppercase tracking-[0.2em] text-[11px] flex items-center gap-1"
          data-testid="verify-resend"
        >
          <RefreshCw size={11} className={resending ? "animate-spin" : ""} /> resend code
        </button>
      </div>
    </AuthLayout>
  );
}
